/** Standard conversion for IV fluids: 1 ml = 20 drops. */
import { formatMedicineCatalogFields } from "./nameFormatUtils";

export const DROPS_PER_ML = 20;

/** Stored medicine type value for IV fluids (legacy spelling in API/DB). */
export const IV_FLUID_MEDICINE_TYPE = "FLuids";

export const MEDICINE_TYPE_OPTIONS = [
  { value: "Homeopathy", label: "Homeopathy" },
  { value: "Allopathy", label: "Allopathy" },
  { value: IV_FLUID_MEDICINE_TYPE, label: "IV-Fluids", ipdOnly: true },
];

/** Canonical display/storage label for a medicine type (Allopathy / Homeopathy / FLuids). */
export const normalizeMedicineTypeLabel = (medicineType) => {
  if (medicineType == null || medicineType === "") return null;
  const str = String(medicineType).trim();
  if (!str || /^n\/?a$/i.test(str)) return null;
  const key = str.toLowerCase().replace(/[\s_-]+/g, "");
  if (key === "homeopathy") return "Homeopathy";
  if (key === "allopathy") return "Allopathy";
  if (key === "fluids" || key === "ivfluids" || key === "fluid") {
    return IV_FLUID_MEDICINE_TYPE;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Department column for issued-medicine invoices should reflect medicine type
 * (Allopathy / Homeopathy), not the consulting doctor's department.
 */
export const resolveMedicineIssueDepartmentName = (medicinesOrType) => {
  if (medicinesOrType == null) return null;
  if (!Array.isArray(medicinesOrType)) {
    return normalizeMedicineTypeLabel(medicinesOrType);
  }

  const unique = [];
  const seen = new Set();
  for (const item of medicinesOrType) {
    const label = normalizeMedicineTypeLabel(
      item?.medicineType ?? item?.medicine_type ?? item?.type ?? item
    );
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(label);
  }
  if (unique.length === 0) return null;
  if (unique.length === 1) return unique[0];
  return unique.join(", ");
};

export const isIvFluidMedicineType = (medicineType) => {
  const normalized = String(medicineType || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  return normalized === "fluids" || normalized === "ivfluids" || normalized === "fluid";
};

/** Premium only — hide IV-Fluids on Basic/Intermediate; keep current value when editing. */
export const filterMedicineTypesForPackage = (
  options = MEDICINE_TYPE_OPTIONS,
  { isPremium = false, keepValue = null } = {}
) => {
  if (isPremium) return options;
  const keep = keepValue != null ? String(keepValue) : "";
  return options.filter((opt) => {
    if (!opt.ipdOnly) return true;
    return keep && String(opt.value) === keep;
  });
};

export const MEDICINE_DETAILS_OPTIONS = ["Box", "Capsule", "Syrup", "Tablet", "Fluid"];

export const normalizeMedicineDetailsKey = (medicineDetails) =>
  String(medicineDetails || "").trim().toLowerCase();

export const isBoxOrSyrupMedicine = (medicineDetails) => {
  const key = normalizeMedicineDetailsKey(medicineDetails);
  return key === "box" || key === "syrup";
};

export const isCapsuleOrTablet = (medicineDetails) => {
  const key = normalizeMedicineDetailsKey(medicineDetails);
  return key === "capsule" || key === "tablet";
};

export const isFluidMedicine = (medicineDetails) =>
  normalizeMedicineDetailsKey(medicineDetails) === "fluid";

/** Medicines where `details` is a countable unit (tablets per strip, drops per bottle). */
export const usesDetailsMultiplier = (medicineDetails) =>
  isCapsuleOrTablet(medicineDetails) || isFluidMedicine(medicineDetails);

export const resolveMedicineDetailsValue = (medicineDetails, detailsInput) => {
  if (isBoxOrSyrupMedicine(medicineDetails)) {
    return 1;
  }
  const parsed = Number(detailsInput);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 1;
  }
  return parsed;
};

/** costPerUnit = price / details (per tablet/capsule or per drop for fluids). */
export const calculateCostPerUnit = (medicineDetails, price, detailsInput) => {
  const priceNum = Number(price);
  const details = resolveMedicineDetailsValue(medicineDetails, detailsInput);
  if (!Number.isFinite(priceNum) || priceNum <= 0 || details <= 0) {
    return 0;
  }
  return priceNum / details;
};

export const formatCostPerUnit = (value, fractionDigits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return num.toFixed(fractionDigits);
};

export const getFluidPricingMeta = (
  price,
  dropsPerBottle,
  dropsPerMl = DROPS_PER_ML
) => {
  const drops = resolveMedicineDetailsValue("fluid", dropsPerBottle);
  const priceNum = Number(price);
  const safeDropsPerMl = Number(dropsPerMl) > 0 ? Number(dropsPerMl) : DROPS_PER_ML;
  const mlPerBottle = drops / safeDropsPerMl;
  const costPerDrop = calculateCostPerUnit("fluid", priceNum, drops);
  const costPerMl = mlPerBottle > 0 && Number.isFinite(priceNum) ? priceNum / mlPerBottle : 0;

  return {
    dropsPerBottle: drops,
    dropsPerMl: safeDropsPerMl,
    mlPerBottle,
    costPerDrop,
    costPerMl,
  };
};

export const buildMedicineSavePayload = (values, userId) => {
  const details = resolveMedicineDetailsValue(values.medicineDetails, values.details);
  const costPerUnit = calculateCostPerUnit(
    values.medicineDetails,
    values.price,
    details
  );

  return formatMedicineCatalogFields({
    medicineName: (values.medicineName || "").toString().trim(),
    medicineType: values.medicineType,
    manufacturer: values.manufacturer,
    medicineDetails: values.medicineDetails,
    details,
    price: Number(values.price),
    costPerUnit,
    userId,
    supplier: values.supplier || "",
  });
};

export const getDetailsFieldLabel = (medicineDetails) => {
  if (isFluidMedicine(medicineDetails)) {
    return "Drops Per Bottle";
  }
  if (isCapsuleOrTablet(medicineDetails)) {
    return "Per Strip Capsules/Tablets";
  }
  return "Details";
};

export const getCostPreviewLabel = (medicineDetails, price, details) => {
  const costPerUnit = calculateCostPerUnit(medicineDetails, price, details);
  if (!costPerUnit) return "";

  if (isFluidMedicine(medicineDetails)) {
    const meta = getFluidPricingMeta(price, details);
    return `Cost per drop: ${formatCostPerUnit(meta.costPerDrop, 4)} | Cost per ml (1 ml = ${meta.dropsPerMl} drops): ${formatCostPerUnit(meta.costPerMl)}`;
  }

  if (isCapsuleOrTablet(medicineDetails)) {
    return `Cost per unit: ${formatCostPerUnit(costPerUnit)}`;
  }

  return "";
};
